import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaMapMarkerAlt, FaStar, FaArrowLeft, FaUserCheck } from 'react-icons/fa';

function CategoryPage() {
  const { categoryName } = useParams();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch workers by category
        const response = await apiClient.get(`/workers?category=${encodeURIComponent(categoryName)}`);
        setWorkers(response.data.workers || []);
      } catch (err) {
        console.error(`Error fetching workers:`, err);
        setError(`Failed to load workers.`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categoryName]);

  const CompactWorkerCard = ({ worker }) => (
      <Link to={`/workers/profile/${worker.profileId}`} className="group bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all flex flex-col items-center text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          
          <img 
              src={loadImage(worker.profile?.picture)} 
              alt={worker.profile?.name} 
              className="w-20 h-20 rounded-full object-cover border-2 border-slate-100 group-hover:border-emerald-500 transition-colors mb-3"
          />
          
          <h3 className="font-bold text-slate-800 text-sm group-hover:text-emerald-700 transition-colors line-clamp-1">{worker.profile?.name || 'Worker'}</h3>
          
          <div className="flex items-center text-xs text-slate-500 mt-1 mb-3 space-x-3">
              <span className="flex items-center"><FaStar className="text-yellow-400 mr-1" /> New</span>
              <span className="flex items-center"><FaMapMarkerAlt className="text-slate-300 mr-1" /> {worker.profile?.location?.district || 'BD'}</span>
          </div>

          <button className="w-full py-2 rounded-lg bg-slate-50 text-slate-600 text-xs font-bold group-hover:bg-emerald-50 group-hover:text-emerald-700 transition-colors">
              View Profile
          </button>
      </Link>
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
        
        {/* Header */}
        <div className="bg-white border-b border-slate-200 pt-24 pb-10 px-6">
            <div className="container mx-auto max-w-7xl">
                <Link to="/categories" className="inline-flex items-center text-slate-400 hover:text-emerald-600 font-bold text-sm mb-4 transition-colors">
                    <FaArrowLeft className="mr-2" /> Back to Categories
                </Link>
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center text-2xl">
                        <FaUserCheck />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-800 tracking-tight">{categoryName}</h1>
                        <p className="text-slate-500 font-medium">{workers.length} professionals found</p>
                    </div>
                </div>
            </div>
        </div>

        {/* Content */}
        <div className="container mx-auto max-w-7xl px-6 py-10">
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 animate-pulse">
                    {[1,2,3,4,5,6,7,8,9,10].map(i => <div key={i} className="h-48 bg-slate-200 rounded-2xl"></div>)}
                </div>
            ) : error ? (
                <div className="p-10 text-center bg-red-50 text-red-600 rounded-3xl font-medium">{error}</div>
            ) : workers.length === 0 ? (
                <div className="text-center py-20">
                    <p className="text-slate-400 text-lg font-medium">No workers found in this category yet.</p>
                    <Link to="/categories" className="text-emerald-600 font-bold hover:underline mt-2 inline-block">Browse other categories</Link>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {workers.map(worker => <CompactWorkerCard key={worker.id} worker={worker} />)}
                </div>
            )}
        </div>
    </div>
  );
}

export default CategoryPage;
